/**
 * Create Job — form to post a new job.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@design-system';

import { createJob } from '../../domains/jobs';

const CATEGORIES = [
  'engineering', 'design', 'product', 'marketing',
  'operations', 'sales', 'consulting', 'other',
];

const JOB_TYPES = [
  'full-time', 'part-time', 'contract', 'freelance', 'co-founder',
];

function CreateJobPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('engineering');
  const [type, setType] = useState('contract');
  const [skillsInput, setSkillsInput] = useState('');
  const [location, setLocation] = useState('');
  const [remote, setRemote] = useState(true);
  const [budget, setBudget] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const skills = skillsInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (skills.length === 0) {
      setError('Please enter at least one skill');
      setSubmitting(false);
      return;
    }

    try {
      const job = await createJob({
        title,
        description,
        category,
        type,
        skills,
        location: location || undefined,
        remote,
        budget: budget || undefined,
        contactEmail: contactEmail || undefined,
      });
      navigate(`/jobs/${job._id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job');
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Post a Job
      </Typography>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} className="flex max-w-2xl flex-col gap-4">
        <TextField
          label="Title"
          required
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          inputProps={{ minLength: 3, maxLength: 120 }}
        />

        <TextField
          label="Description"
          required
          multiline
          rows={6}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ minLength: 10, maxLength: 5000 }}
          placeholder="Describe the role, responsibilities, and requirements…"
        />

        <Box className="flex gap-4">
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value as string)}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value as string)}
            >
              {JOB_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="Skills (comma-separated)"
          required
          fullWidth
          value={skillsInput}
          onChange={(e) => setSkillsInput(e.target.value)}
          placeholder="React, Node.js, MongoDB"
          helperText="Enter at least one skill, separated by commas"
        />

        <Box className="flex items-center gap-4">
          <TextField
            label="Location"
            fullWidth
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. San Francisco, CA"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={remote}
                onChange={(e) => setRemote(e.target.checked)}
              />
            }
            label="Remote OK"
          />
        </Box>

        <TextField
          label="Budget (optional)"
          fullWidth
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          placeholder="e.g. $5,000 - $10,000"
        />

        <TextField
          label="Contact Email (optional)"
          type="email"
          fullWidth
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />

        <Box className="flex gap-2">
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || title.length < 3 || description.length < 10}
          >
            {submitting ? 'Posting…' : 'Post Job'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/jobs')}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default CreateJobPage;
